import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db, hasConfig } from '../auth/firebaseClient';
import { PROFILE_DEFINITIONS, getAllowedProfiles as getAllowedProfilesFallback, hasUserAccessToProfile as hasUserAccessFallback } from './profileAccess';

const normalize = (value) => String(value || '').trim().toLowerCase();

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const isExpired = (expiresAt) => {
  const millis = toMillis(expiresAt);
  if (!millis) return false;
  return millis <= Date.now();
};

export const getAlbumBySlugRecord = async (slug) => {
  if (!hasConfig || !db) return null;
  const safeSlug = normalize(slug);
  if (!safeSlug) return null;

  const albumsRef = collection(db, 'albums');
  const albumQuery = query(albumsRef, where('slug', '==', safeSlug), limit(1));
  const snap = await getDocs(albumQuery);
  if (snap.empty) return null;
  const found = snap.docs[0];
  return { id: found.id, ...found.data() };
};

export const getAlbumByIdRecord = async (albumId) => {
  if (!hasConfig || !db || !albumId) return null;
  const albumDoc = await getDoc(doc(db, 'albums', albumId));
  if (!albumDoc.exists()) return null;
  return { id: albumDoc.id, ...albumDoc.data() };
};

export const getInviteByToken = async (token) => {
  if (!hasConfig || !db) return { status: 'invalid', invite: null, inviteDocId: null };
  const safeToken = String(token || '').trim();
  if (!safeToken) return { status: 'invalid', invite: null, inviteDocId: null };

  const invitesRef = collection(db, 'invites');
  const inviteQuery = query(invitesRef, where('token', '==', safeToken), limit(1));
  let inviteDoc = null;
  const querySnap = await getDocs(inviteQuery);

  if (!querySnap.empty) {
    inviteDoc = querySnap.docs[0];
  } else {
    const directDoc = await getDoc(doc(db, 'invites', safeToken));
    if (directDoc.exists()) {
      inviteDoc = directDoc;
    }
  }

  if (!inviteDoc) return { status: 'invalid', invite: null, inviteDocId: null };

  const invite = inviteDoc.data() || {};
  if (invite.used) return { status: 'used', invite, inviteDocId: inviteDoc.id };
  if (isExpired(invite.expiresAt)) return { status: 'expired', invite, inviteDocId: inviteDoc.id };
  return { status: 'valid', invite, inviteDocId: inviteDoc.id };
};

export const ensureAlbumAccess = async ({
  albumId,
  userUid,
  role = 'viewer',
}) => {
  if (!hasConfig || !db || !albumId || !userUid) return;
  const entryId = `${albumId}_${userUid}`;
  await setDoc(doc(db, 'albumAccess', entryId), {
    albumId,
    userUid,
    role,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const markInviteAsUsed = async (inviteDocId) => {
  if (!hasConfig || !db || !inviteDocId) return;
  await updateDoc(doc(db, 'invites', inviteDocId), {
    used: true,
    usedAt: serverTimestamp(),
  });
};

export const hasUserAccessToSlug = async (user, slug) => {
  if (!user) return false;
  const fallbackAllowed = hasUserAccessFallback(user, slug);
  if (fallbackAllowed) return true;
  if (!hasConfig || !db) return false;

  const album = await getAlbumBySlugRecord(slug);
  if (!album) return false;

  const accessRef = collection(db, 'albumAccess');
  const accessQuery = query(
    accessRef,
    where('albumId', '==', album.id),
    where('userUid', '==', user.uid),
    limit(1),
  );
  const accessSnap = await getDocs(accessQuery);
  return !accessSnap.empty;
};

export const getAllowedProfilesForUser = async (user) => {
  if (!user) return [];
  const fallbackProfiles = getAllowedProfilesFallback(user);
  if (!hasConfig || !db) return fallbackProfiles;

  const accessRef = collection(db, 'albumAccess');
  const accessQuery = query(accessRef, where('userUid', '==', user.uid));
  const accessSnap = await getDocs(accessQuery);
  if (accessSnap.empty) return fallbackProfiles;

  const albumIds = [...new Set(accessSnap.docs.map((entry) => entry.data()?.albumId).filter(Boolean))];
  if (albumIds.length === 0) return fallbackProfiles;

  const albumRecords = await Promise.all(albumIds.map(async (albumId) => {
    const albumDoc = await getDoc(doc(db, 'albums', albumId));
    if (!albumDoc.exists()) return null;
    return { id: albumDoc.id, ...albumDoc.data() };
  }));

  const profileMap = new Map(PROFILE_DEFINITIONS.map((profile) => [normalize(profile.slug), profile]));
  const firestoreProfiles = albumRecords
    .filter(Boolean)
    .map((album) => profileMap.get(normalize(album.slug)))
    .filter(Boolean);

  const merged = [...fallbackProfiles, ...firestoreProfiles];
  const uniqueBySlug = new Map(merged.map((profile) => [profile.slug, profile]));
  return [...uniqueBySlug.values()];
};
